<i18n src="./resources/locales.json"></i18n>

<template>
  <v-container>
    <h1 class="ml-3 primary--text">{{ $t('workReportTitle') }}</h1>
    <h2 class="ml-3 primary--text text-body-1">
      {{ $t('workReportSubtitle') }}
    </h2>
    <p v-if="periodLabel" class="ml-3 text--secondary">
      {{ $t('periodShown') }}: {{ periodLabel }}
    </p>

    <v-row
      class="tool-bar mt-6 ml-10 mb-4 mr-10 align-center"
      no-gutters
      style="flex-wrap: wrap"
    >
      <v-select
        v-model="periodPreset"
        :items="periodPresetOptions"
        item-text="label"
        item-value="value"
        :label="$t('reportPeriod')"
        background-color="primaryVariant2"
        outlined
        dense
        hide-details
        flat
        class="work-report-filter mr-4 mb-2"
      />

      <v-select
        v-model="statusFilter"
        :items="statusOptions"
        item-text="label"
        item-value="value"
        :label="$t('workStatusFilter')"
        background-color="primaryVariant2"
        outlined
        dense
        hide-details
        flat
        class="work-report-filter work-report-filter--narrow mr-4 mb-2"
      />

      <v-btn
        color="primary"
        depressed
        class="mb-2"
        :loading="getIsWeeklyWorkReportLoading"
        @click="fetchReport"
      >
        {{ $t('generateReport') }}
      </v-btn>
      <v-btn
        outlined
        color="primary"
        class="mb-2 ml-0 ml-sm-2"
        :disabled="getIsWeeklyWorkReportLoading"
        :loading="printPdfLoading"
        @click="printPdf"
      >
        <v-icon left small>mdi-printer</v-icon>
        {{ $t('print') }}
      </v-btn>
    </v-row>

    <v-row>
      <v-col class="ml-13">
        <v-data-table
          :headers="headers"
          :items="tableItems"
          :footer-props="{
            itemsPerPageOptions: [10, 20, 50],
            itemsPerPageText: $t('rowsPerPage')
          }"
          :items-per-page="20"
          class="elevation-1 ma-8"
          @click:row="openOrderDetail"
        >
          <template v-slot:[`footer.page-text`]="items">
            {{ items.pageStart }} - {{ items.pageStop }} {{ $t('of') }}
            {{ items.itemsLength }}
          </template>
          <template v-slot:[`item.orderNumber`]="{ item }">
            {{ formatOrderNumber(item.orderNumber) }}
          </template>
          <template v-slot:[`item.workerName`]="{ item }">
            {{ item.workerName }}
          </template>
          <template v-slot:[`item.assignedDateTime`]="{ item }">
            {{ formatDateTime(item.assignedDateTime) }}
          </template>
          <template v-slot:[`item.deliverDateTime`]="{ item }">
            {{ formatDateTime(item.deliverDateTime) }}
          </template>
          <template v-slot:[`item.finishedDateTime`]="{ item }">
            {{ formatDateTime(item.finishedDateTime) }}
          </template>
          <template v-slot:no-data>
            <p>{{ $t('noDataMessage') }}</p>
          </template>
        </v-data-table>
        <LoadingMask :isLoading="getIsWeeklyWorkReportLoading" />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import moment from 'moment';
import LoadingMask from '@/components/reusable-components/loading-mask/LoadingMask.vue';

export default {
  name: 'WorkReport',

  components: {
    LoadingMask
  },

  data() {
    return {
      periodPreset: 'thisWeek',
      statusFilter: 'pending',
      printPdfLoading: false
    };
  },

  computed: {
    ...mapGetters('reports', ['weeklyWorkReport']),
    ...mapGetters('loading', ['getIsWeeklyWorkReportLoading']),
    ...mapGetters('orders', ['getOrderById']),

    periodPresetOptions() {
      return [
        { value: 'nextWeek', label: this.$t('periodNextWeek') },
        { value: 'thisWeek', label: this.$t('periodThisWeek') },
        { value: 'lastWeek', label: this.$t('periodLastWeek') },
        { value: 'last4Weeks', label: this.$t('periodLast4Weeks') },
        { value: 'last6Weeks', label: this.$t('periodLast6Weeks') }
      ];
    },

    selectedPeriodRange() {
      const today = moment();
      let start;
      let end;
      switch (this.periodPreset) {
        case 'nextWeek':
          start = today.clone().add(1, 'week').startOf('isoWeek');
          end = today.clone().add(1, 'week').endOf('isoWeek');
          break;
        case 'lastWeek':
          start = today.clone().subtract(1, 'week').startOf('isoWeek');
          end = today.clone().subtract(1, 'week').endOf('isoWeek');
          break;
        case 'last4Weeks':
          start = today.clone().startOf('isoWeek').subtract(3, 'weeks');
          end = today.clone().endOf('isoWeek');
          break;
        case 'last6Weeks':
          start = today.clone().startOf('isoWeek').subtract(5, 'weeks');
          end = today.clone().endOf('isoWeek');
          break;
        case 'thisWeek':
        default:
          start = today.clone().startOf('isoWeek');
          end = today.clone().endOf('isoWeek');
      }
      return {
        fromDate: start.format('YYYY-MM-DD'),
        toDate: end.format('YYYY-MM-DD')
      };
    },

    statusOptions() {
      return [
        { value: 'pending', label: this.$t('statusPending') },
        { value: 'completed', label: this.$t('statusCompleted') }
      ];
    },

    normalizedReport() {
      const r = this.weeklyWorkReport;
      if (!r) return null;
      return {
        periodStart: r.periodStart || r.PeriodStart,
        periodEnd: r.periodEnd || r.PeriodEnd,
        pendingAssignments: r.pendingAssignments || r.PendingAssignments || [],
        deliveredInPeriod: r.deliveredInPeriod || r.DeliveredInPeriod || []
      };
    },

    periodLabel() {
      const r = this.normalizedReport;
      if (r && r.periodStart && r.periodEnd) {
        const start = this.convertApiDate(r.periodStart);
        const end = this.convertApiDate(r.periodEnd);
        if (start && end) return `${start} — ${end}`;
      }
      const { fromDate, toDate } = this.selectedPeriodRange;
      const a = moment(fromDate).format('DD/MM/YYYY');
      const b = moment(toDate).format('DD/MM/YYYY');
      return a === b ? a : `${a} — ${b}`;
    },

    headers() {
      const base = [
        {
          text: this.$t('orderNumber'),
          value: 'orderNumber',
          align: 'center',
          sortable: true,
          class: 'primary--text'
        },
        {
          text: this.$t('worker'),
          value: 'workerName',
          align: 'center',
          sortable: true,
          class: 'primary--text'
        },
        {
          text: this.$t('service'),
          value: 'serviceName',
          align: 'center',
          sortable: true,
          class: 'primary--text'
        },
        {
          text: this.$t('subService'),
          value: 'subServiceName',
          align: 'center',
          sortable: false,
          class: 'primary--text'
        },
        {
          text: this.$t('jobPrice'),
          value: 'price',
          align: 'right',
          sortable: true,
          class: 'primary--text'
        },
        {
          text: this.$t('assignedAt'),
          value: 'assignedDateTime',
          align: 'center',
          sortable: true,
          class: 'primary--text'
        },
        {
          text: this.$t('promisedServiceDelivery'),
          value: 'deliverDateTime',
          align: 'center',
          sortable: true,
          class: 'primary--text'
        }
      ];

      if (this.statusFilter === 'completed') {
        base.push({
          text: this.$t('finishedAt'),
          value: 'finishedDateTime',
          align: 'center',
          sortable: true,
          class: 'primary--text'
        });
      }

      return base;
    },

    tableItems() {
      const r = this.normalizedReport;
      if (!r) return [];

      const raw =
        this.statusFilter === 'pending'
          ? r.pendingAssignments
          : r.deliveredInPeriod;

      const list = Array.isArray(raw) ? raw : [];
      return list.map(row => {
        const w = row.worker || row.Worker;
        return {
          ...row,
          orderId: row.orderId || row.OrderId,
          orderNumber: row.orderNumber ?? row.OrderNumber,
          serviceName: row.serviceName || row.ServiceName || '—',
          price: this.formatPrice(row.price ?? row.Price),
          assignedDateTime: row.assignedDateTime ?? row.AssignedDateTime,
          deliverDateTime: row.deliverDateTime ?? row.DeliverDateTime,
          finishedDateTime: row.finishedDateTime ?? row.FinishedDateTime,
          worker: w,
          workerName: w && w.name ? w.name : '—',
          subServiceName: row.subServiceName || row.SubServiceName || '—'
        };
      });
    }
  },

  created() {
    this.$root.$on('work-report-print-loading', this.setPrintPdfLoading);
    this.fetchReport();
  },

  beforeDestroy() {
    this.$root.$off('work-report-print-loading', this.setPrintPdfLoading);
  },

  methods: {
    ...mapActions('reports', ['retrieveWeeklyWorkReport']),
    ...mapActions('orders', ['retrieveOrderById']),

    fetchReport() {
      const { fromDate, toDate } = this.selectedPeriodRange;
      this.retrieveWeeklyWorkReport({ fromDate, toDate });
    },

    convertApiDate(value) {
      if (!value) return '';
      return moment(value).format('DD/MM/YYYY');
    },

    formatDateTime(value) {
      if (value == null || value === '') return '—';
      return moment(value).format('DD/MM/YYYY HH:mm');
    },

    formatPrice(value) {
      if (value == null || value === '') return '—';
      const number = Number(value);
      if (Number.isNaN(number)) return '—';
      return number.toFixed(2);
    },

    formatOrderNumber(orderNumber) {
      if (orderNumber == null || orderNumber === '') return '—';
      const s = String(orderNumber);
      return s.length < 5 ? this.formatOrderNumber('0' + s) : s;
    },

    setPrintPdfLoading(value) {
      this.printPdfLoading = Boolean(value);
    },

    printCellValue(item, valueKey) {
      switch (valueKey) {
        case 'orderNumber':
          return this.formatOrderNumber(item.orderNumber);
        case 'assignedDateTime':
        case 'deliverDateTime':
        case 'finishedDateTime':
          return this.formatDateTime(item[valueKey]);
        default: {
          const v = item[valueKey];
          if (v == null || v === '') return '—';
          return String(v);
        }
      }
    },

    printPdf() {
      const columnLabels = this.headers.map(h => h.text);
      const rows = this.tableItems.map(item =>
        this.headers.map(h => this.printCellValue(item, h.value))
      );
      const statusLabel =
        this.statusFilter === 'pending'
          ? this.$t('statusPending')
          : this.$t('statusCompleted');
      this.$root.$emit('print-work-report-pdf', {
        fileName: `ReporteTrabajos_${this.statusFilter}`,
        title: this.$t('workReportTitle'),
        subtitle: this.$t('workReportSubtitle'),
        periodLine: `${this.$t('periodShown')}: ${this.periodLabel}`,
        statusLine: `${this.$t('workStatusFilter')}: ${statusLabel}`,
        columnLabels,
        rows,
        emptyMessage: this.$t('noDataMessage')
      });
    },

    async openOrderDetail(row) {
      const id = row.orderId;
      if (!id) return;
      await this.retrieveOrderById(id);
      this.$root.$emit(
        'open-order-detail-modal-info',
        Object.assign({}, this.getOrderById),
        true
      );
    }
  }
};
</script>

<style scoped>
@import './ScopedStyles.css';
</style>
